// src/context/RecruitmentContext.js
import React, { createContext, useContext, useReducer, useMemo } from "react";

const AdviserContext = createContext(null);

const initialState = {
  docId: null,
  data: null, // { baseData, personalDetails, applicationDetails, ... }
  savedAt: null,
  loading: false,
  error: null,
};

// in your reducer file
const getDeep = (obj, path) =>
  (path || "")
    .split(".")
    .filter(Boolean)
    .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);

const setDeep = (obj, path, value) => {
  const parts = (path || "").split(".").filter(Boolean);
  if (!parts.length) return obj;
  const last = parts.pop();
  let cur = obj;
  for (const p of parts) {
    if (typeof cur[p] !== "object" || cur[p] === null) cur[p] = {};
    cur = cur[p];
  }
  cur[last] = value;
  return obj;
};


function reducer(state, action) {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };

    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };

    case "SET_ADVISER_DATA":
      return {
        ...state,
        docId: action.payload.docId,
        data: action.payload.data,
        savedAt: Date.now(),
        loading: false,
        error: null,
      };

    // PATCH a whole section shallowly, e.g. { section:'personalDetails', patch:{ forename:'Alice' } }
    case "PATCH_SECTION": {
      // Back-compat: if someone still dispatches {section, patch}
      if (
        action.payload?.section &&
        action.payload?.patch &&
        !action.payload.sectionPath
      ) {
        const { section, patch } = action.payload;
        const next = { ...(state.data || {}) };
        next[section] = { ...(next[section] || {}), ...(patch || {}) };
        // console.log("[reducer] PATCH_SECTION (legacy):", {
        //   section,
        //   patch,
        //   next,
        // });
        return { ...state, data: next };
      }

      const { sectionPath, fieldPath, index, value } = action.payload || {};
      const next = { ...(state.data || {}) };
      let target = getDeep(next, sectionPath);

      if (Array.isArray(target) && index != null) {
        const arr = [...target];
        const item = { ...(arr[index] || {}) };
        // fieldPath can be nested (e.g. "nested.key")
        if (fieldPath.includes(".")) {
          const itemClone = { ...item };
          setDeep(itemClone, fieldPath, value);
          arr[index] = itemClone;
        } else {
          arr[index] = { ...item, [fieldPath]: value };
        }
        setDeep(next, sectionPath, arr);
      } else {
        // object target
        const curObj = { ...(target || {}) };
        if (fieldPath.includes(".")) {
          const curClone = { ...curObj };
          setDeep(curClone, fieldPath, value);
          setDeep(next, sectionPath, curClone);
        } else {
          setDeep(next, sectionPath, { ...curObj, [fieldPath]: value });
        }
      }

      // console.log("[reducer] PATCH_SECTION (path):", {
      //   sectionPath,
      //   fieldPath,
      //   index,
      //   value,
      //   next,
      // });
      return { ...state, data: next };
    }

    // PATCH by a deep path, e.g. { path:['applicationDetails','addresses','home'], value:'...' }
    case "PATCH_BY_PATH": {
      const { path, value } = action.payload;
      const data = state.data || {};
      return {
        ...state,
        data: setDeep(data, path, value),
        savedAt: Date.now(),
      };
    }

    case "RESET":
      return initialState;

    default:
      return state;
  }
}

export function AdviserProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return (
    <AdviserContext.Provider value={value}>{children}</AdviserContext.Provider>
  );
}

export const useAdviserContext = () => {
  const ctx = useContext(AdviserContext);
  if (!ctx)
    throw new Error("useAdviserContext must be used within AdviserProvider");
  return ctx;
};
